import { Alert, Button, Modal, ProgressBar, Spinner } from "react-bootstrap";
import { useMemo, useState } from "react";
import styles from "../styles/DocumentUpload.module.css";
import DocumentUploader from "./documentuploader";
import DocumentListSkeleton from "./skeletons/documentlistskeleton";
import moment from "moment";
import { useOutletContext } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowsRotate, faFileArrowDown, faFolderOpen } from "@fortawesome/free-solid-svg-icons";
import { faTrashCan } from "@fortawesome/free-regular-svg-icons";
import {
    useDeleteDocument,
    useDocumentsQuery,
    useDownloadDocument
} from "../new/query";

const DocumentList = () => {
    const [feedback, setFeedback] = useState(null);
    const [pendingDelete, setPendingDelete] = useState(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [downloadState, setDownloadState] = useState({});
    const [activeTypeFilter, setActiveTypeFilter] = useState("all");
    const { ApplicationID: applicationId, pagePermissions = {} } = useOutletContext();
    const canAddDocument = pagePermissions.canAdd ?? true;
    const canDeleteDocument = pagePermissions.canDelete ?? true;
    const documentsQuery = useDocumentsQuery(applicationId);

    const downloadMutation = useDownloadDocument(
        (blob, variables) => {
            const url = window.URL.createObjectURL(new Blob([blob]));
            const link = document.createElement("a");

            link.href = url;
            link.setAttribute("download", variables.fileName);

            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
            setDownloadState((current) => {
                const next = { ...current };
                delete next[variables.DocumentId];
                return next;
            });
        },
        (_error, variables) => {
            setDownloadState((current) => {
                const next = { ...current };
                delete next[variables.DocumentId];
                return next;
            });
            setFeedback({
                type: "error",
                message: "Unable to download the requested document."
            });
        }
    );

    const deleteMutation = useDeleteDocument(
        applicationId,
        () => {
            setIsDeleteModalOpen(false);
            setFeedback({
                type: "success",
                message: "Document deleted successfully."
            });
        },
        () => {
            setFeedback({
                type: "error",
                message: "Unable to delete the selected document."
            });
        }
    );

    const rawDocumentTypes = useMemo(() => documentsQuery.data ?? [], [documentsQuery.data]);
    const fileTypeList = useMemo(() => {
        const types = rawDocumentTypes.map((type) => ({
            id: type.id,
            title: type.name,
            description: type.description,
            validTypes: type.validTypes
        }));

        return types.sort((a, b) => a.title.localeCompare(b.title));
    }, [rawDocumentTypes]);

    const files = useMemo(() => {
        const filesWithType = [];

        rawDocumentTypes.forEach((type) => {
            type.documents.forEach((file) => {
                filesWithType.push({
                    ...file,
                    name: file.name,
                    type: type.name
                });
            });
        });

        return filesWithType.sort(
            (a, b) =>
                moment(b.uploadDate, "M/D/YYYY h:mm:ss A").valueOf() -
                moment(a.uploadDate, "M/D/YYYY h:mm:ss A").valueOf()
        );
    }, [rawDocumentTypes]);

    const typeFilters = useMemo(() => {
        const counts = files.reduce((acc, file) => {
            acc[file.type] = (acc[file.type] ?? 0) + 1;
            return acc;
        }, {});

        return Object.entries(counts)
            .map(([type, count]) => ({ type, count }))
            .sort((a, b) => a.type.localeCompare(b.type));
    }, [files]);

    const filteredFiles = useMemo(() => {
        if (activeTypeFilter === "all") {
            return files;
        }

        return files.filter((file) => file.type === activeTypeFilter);
    }, [activeTypeFilter, files]);

    const handleDownload = (fileId, fileName) => {
        if (downloadState[fileId]?.isDownloading) {
            return;
        }

        setDownloadState((current) => ({
            ...current,
            [fileId]: {
                isDownloading: true,
                progress: 0
            }
        }));

        downloadMutation.mutate({
            DocumentId: fileId,
            fileName,
            onDownloadProgress: (event) => {
                if (!event.total) {
                    return;
                }

                setDownloadState((current) => ({
                    ...current,
                    [fileId]: {
                        isDownloading: true,
                        progress: Math.round((event.loaded / event.total) * 100)
                    }
                }));
            }
        });
    };

    const handleDelete = async () => {
        if (!pendingDelete) {
            return;
        }

        deleteMutation.mutate({ DocumentId: pendingDelete.id });
    };

    const latestUpload = useMemo(() => (files.length > 0 ? files[0].uploadDate : "None yet"), [files]);
    const typeCount = fileTypeList.length;

    if (documentsQuery.isLoading && !documentsQuery.data) {
        return <DocumentListSkeleton />;
    }

    return (
        <div className={styles.pageStack}>
            <div className={`${styles.heroCard} clientCard`}>
                <div className={styles.heroInner}>
                    <div className={styles.heroTitleRow}>
                        <div>
                            <p className={styles.heroEyebrow}>Document workspace</p>
                            <h2 className={styles.heroTitle}>Upload and manage case documents</h2>
                            <p className={styles.heroSubtitle}>
                                Use the upload panel as the main entry point, then review, download, or remove files
                                from the document cards below.
                            </p>
                        </div>
                        <Button
                            type="button"
                            variant="light"
                            className={styles.heroRefresh}
                            onClick={() => documentsQuery.refetch()}
                            disabled={documentsQuery.isFetching}
                        >
                            <FontAwesomeIcon icon={faArrowsRotate} className="me-2" />
                            Refresh
                        </Button>
                    </div>

                    <div className={styles.heroStats}>
                        <div className={styles.heroStat}>
                            <span className={styles.heroStatValue}>{files.length}</span>
                            <span className={styles.heroStatLabel}>Uploaded files</span>
                        </div>
                        <div className={styles.heroStat}>
                            <span className={styles.heroStatValue}>{typeCount}</span>
                            <span className={styles.heroStatLabel}>Document types</span>
                        </div>
                        <div className={styles.heroStat}>
                            <span className={styles.heroStatValue}>{latestUpload}</span>
                            <span className={styles.heroStatLabel}>Latest upload</span>
                        </div>
                    </div>
                </div>
            </div>

            {feedback ? (
                <Alert
                    variant={feedback.type === "error" ? "danger" : "success"}
                    className={styles.feedbackBanner}
                    dismissible
                    onClose={() => setFeedback(null)}
                >
                    {feedback.message}
                </Alert>
            ) : null}

            <div className={`${styles.workspaceLayout} ${!canAddDocument ? styles.workspaceLayoutFull : ""}`}>
                {canAddDocument ? (
                    <div className={styles.workspaceSidebar}>
                        <DocumentUploader
                            fileTypes={fileTypeList}
                            applicationId={applicationId}
                            onFeedback={setFeedback}
                        />
                    </div>
                ) : null}

                <div className={styles.workspaceContent}>
                    {typeFilters.length > 0 ? (
                        <div className={styles.filterBar}>
                            <button
                                type="button"
                                className={`${styles.filterPill} ${activeTypeFilter === "all" ? styles.filterPillActive : ""}`}
                                onClick={() => setActiveTypeFilter("all")}
                            >
                                <span>All</span>
                                <span className={styles.filterPillCount}>{files.length}</span>
                            </button>
                            {typeFilters.map((item) => (
                                <button
                                    key={item.type}
                                    type="button"
                                    className={`${styles.filterPill} ${activeTypeFilter === item.type ? styles.filterPillActive : ""}`}
                                    onClick={() => setActiveTypeFilter(item.type)}
                                >
                                    <span>{item.type}</span>
                                    <span className={styles.filterPillCount}>{item.count}</span>
                                </button>
                            ))}
                        </div>
                    ) : null}

                    <div className={styles.documentGrid}>
                        {filteredFiles.length === 0 ? (
                            <div className={styles.emptyCard}>
                                <div className={styles.emptyCardInner}>
                                    <div className={styles.uploadDockIcon} aria-hidden="true">
                                        <FontAwesomeIcon icon={faFolderOpen} size="lg" />
                                    </div>
                                    <div>
                                        <h3 className={styles.emptyCardTitle}>
                                            {files.length === 0 ? "No documents uploaded yet" : "No documents match this filter"}
                                        </h3>
                                        <p className={styles.emptyCardCopy}>
                                            {files.length === 0
                                                ? "Start with the upload panel to add the first document. New files will appear here immediately after upload."
                                                : "Choose another document type filter or switch back to All to see more files."}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            filteredFiles.map((file) => (
                                (() => {
                                    const fileDownloadState = downloadState[file.id];
                                    const isFileDownloading = !!fileDownloadState?.isDownloading;
                                    const fileProgress = fileDownloadState?.progress ?? 0;

                                    return (
                                        <div key={file.id} className={styles.documentCard}>
                                            <div className={styles.documentCardInner}>
                                                <div className={styles.documentCardTop}>
                                                    <div>
                                                        <span className={styles.documentTypePill}>{file.type}</span>
                                                        <Button
                                                            type="button"
                                                            variant="link"
                                                            className={styles.fileDownloadLinkText}
                                                            title={file.name}
                                                            aria-label={`Download ${file.name}`}
                                                            disabled={isFileDownloading}
                                                            onClick={() => handleDownload(file.id, file.name)}
                                                        >
                                                            {file.name}
                                                        </Button>
                                                    </div>
                                                    <Button
                                                        type="button"
                                                        variant="outline-primary"
                                                        size="sm"
                                                        className={styles.documentActionButton}
                                                        disabled={isFileDownloading}
                                                        onClick={() => handleDownload(file.id, file.name)}
                                                    >
                                                        <FontAwesomeIcon icon={faFileArrowDown} className="me-2" />
                                                        {isFileDownloading ? "Downloading..." : "Download"}
                                                    </Button>
                                                </div>

                                                <div className={styles.documentMetaList}>
                                                    {isFileDownloading ? (
                                                        <div className={styles.documentDownloadProgress}>
                                                            <div className={styles.documentDownloadProgressHeader}>
                                                                <span>Downloading</span>
                                                                <span>{fileProgress}%</span>
                                                            </div>
                                                            <ProgressBar now={fileProgress} animated={fileProgress < 100} />
                                                        </div>
                                                    ) : null}
                                                    <div className={styles.documentMetaItem}>
                                                        <span className={styles.documentMetaLabel}>Uploaded Date</span>
                                                        <span className={styles.documentMetaValue}>{file.uploadDate}</span>
                                                    </div>
                                                    <div className={styles.documentMetaItem}>
                                                        <span className={styles.documentMetaLabel}>Uploaded By</span>
                                                        <span className={styles.documentMetaValue}>{file.uploadedBy}</span>
                                                    </div>
                                                </div>

                                                {canDeleteDocument ? (
                                                    <div className={styles.fileCardFooter}>
                                                        <Button
                                                            type="button"
                                                            variant="link"
                                                            className={styles.documentDeleteButton}
                                                            disabled={deleteMutation.isPending}
                                                            onClick={() => {
                                                                setPendingDelete(file);
                                                                setIsDeleteModalOpen(true);
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={faTrashCan} className="me-2" />
                                                            Remove document
                                                        </Button>
                                                    </div>
                                                ) : null}
                                            </div>
                                        </div>
                                    );
                                })()
                            ))
                        )}
                    </div>
                </div>
            </div>

            <Modal
                show={isDeleteModalOpen}
                onHide={() => !deleteMutation.isPending && setIsDeleteModalOpen(false)}
                onExited={() => setPendingDelete(null)}
                centered
                fullscreen="md-down"
                className={styles.deleteModal}
                backdropClassName={styles.deleteModalBackdrop}
                >
                <Modal.Header closeButton={!deleteMutation.isPending} className={styles.deleteModalHeader}>
                    <Modal.Title>Delete document</Modal.Title>
                </Modal.Header>
                <Modal.Body className={styles.deleteModalBody}>
                    <p className={styles.deleteModalCopy}>
                        This will permanently remove the selected document from the application.
                    </p>
                    {pendingDelete ? (
                        <div className={styles.deleteModalCard}>
                            <span className={styles.documentTypePill}>{pendingDelete.type}</span>
                            <div className={styles.deleteModalFileName}>{pendingDelete.name}</div>
                            <div className={styles.deleteModalMeta}>
                                Uploaded {pendingDelete.uploadDate} by {pendingDelete.uploadedBy}
                            </div>
                        </div>
                    ) : null}
                </Modal.Body>
                <Modal.Footer className={styles.deleteModalFooter}>
                    <Button variant="outline-secondary" onClick={() => setIsDeleteModalOpen(false)} disabled={deleteMutation.isPending}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleDelete} disabled={deleteMutation.isPending}>
                        {deleteMutation.isPending ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Deleting...
                            </>
                        ) : (
                            "Delete document"
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default DocumentList;
